import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query";

/** Default micro-batch window. A hub burst inside it becomes one refetch per key. */
export const INVALIDATION_WINDOW_MS = 250;

export interface InvalidationCoalescer {
    /** Mark `queryKey` for invalidation on the next flush. Duplicate keys collapse. */
    readonly invalidate: (queryKey: QueryKey) => void;
    /** Run the pending invalidations now. No-op when nothing is pending. */
    readonly flush: () => void;
    /** Flush what is pending and cancel the timer. Call from the effect cleanup. */
    readonly dispose: () => void;
}

/**
 * Micro-batches `queryClient.invalidateQueries` calls. A realtime hub can
 * deliver many events in a few milliseconds (a completion cascade, a
 * reconnect replay); invalidating on each one makes the active observers
 * refetch N times and the lists repaint N times. The coalescer holds the
 * keys for `windowMs` and issues one `invalidateQueries` per distinct key,
 * so the UI converges in a single refetch.
 *
 * Only invalidation is deferred. Cache patches (`setQueriesData`) stay
 * synchronous in the callers because they are the fast path.
 */
export function createInvalidationCoalescer(
    queryClient: QueryClient,
    windowMs: number = INVALIDATION_WINDOW_MS,
): InvalidationCoalescer {
    const pending = new Map<string, QueryKey>();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const flush = (): void => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        if (pending.size === 0) return;
        const keys = [...pending.values()];
        pending.clear();
        for (const queryKey of keys) {
            void queryClient.invalidateQueries({ queryKey });
        }
    };

    const invalidate = (queryKey: QueryKey): void => {
        pending.set(hashKey(queryKey), queryKey);
        timer ??= setTimeout(flush, windowMs);
    };

    const dispose = (): void => {
        flush();
    };

    return { invalidate, flush, dispose };
}
